import type { ExamQuestionReportItem } from '../api/exam'

/** A single question (not part of a group) */
export interface SingleItem {
  kind: 'single'
  question: ExamQuestionReportItem
  /** Index in the original flat questions array */
  flatIndex: number
}

/** A composite group (passage + sub-questions) */
export interface GroupItem {
  kind: 'group'
  groupId: string
  groupType: string
  passageContent: string | null
  audioUrl: string | null
  groupImages: string[] | null
  groupOptions: { label: string; content: string }[] | null
  subQuestions: ExamQuestionReportItem[]
  /** Indices in the original flat questions array */
  flatIndices: number[]
}

export type QuestionItem = SingleItem | GroupItem

/**
 * Groups a flat array of questions by group_id.
 * Questions with the same non-null group_id are collected into a GroupItem.
 * Questions without a group_id become SingleItems.
 * Preserves the original ordering.
 */
export function groupQuestions(questions: ExamQuestionReportItem[]): QuestionItem[] {
  const items: QuestionItem[] = []
  const groupMap = new Map<string, GroupItem>()

  questions.forEach((q, flatIndex) => {
    const gid = q.group_id

    if (gid) {
      let group = groupMap.get(gid)
      if (!group) {
        group = {
          kind: 'group',
          groupId: gid,
          groupType: q.group_type || 'reading_comp',
          passageContent: q.passage_content || null,
          audioUrl: q.audio_url || null,
          groupImages: q.group_images || null,
          groupOptions: q.group_options || null,
          subQuestions: [],
          flatIndices: [],
        }
        groupMap.set(gid, group)
        items.push(group)
      }
      group.subQuestions.push(q)
      group.flatIndices.push(flatIndex)
    } else {
      items.push({ kind: 'single', question: q, flatIndex })
    }
  })

  return items
}

/**
 * Get the passage title for display in sidebar.
 * Truncates to first line or 50 chars.
 */
export function getGroupTitle(group: GroupItem): string {
  const passage = group.passageContent || ''
  const firstLine = passage.split('\n')[0] || ''
  const typeLabel: Record<string, string> = {
    reading_comp: '阅读理解',
    cloze: '完形填空',
    listening: '听力',
    seven_choose_five: '七选五',
    grammar_fill: '语法填空',
    matching: '信息匹配',
  }
  const label = typeLabel[group.groupType] || group.groupType
  const truncated = firstLine.length > 40 ? firstLine.slice(0, 40) + '...' : firstLine
  return `${label}: ${truncated || `(${group.subQuestions.length}题)`}`
}
